import type { Recipe } from "@/api/recipes";
import { Button } from "@/components/ui/button";

interface RecipeDetailProps {
  recipe: Recipe;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function RecipeDetail({ recipe, onBack, onEdit, onDelete }: RecipeDetailProps) {
  const date = new Date(recipe.createdAt);
  const formattedDate = date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 mb-6 group"
          style={{ fontFamily: "var(--font-body)" }}
        >
          <svg
            className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform duration-200"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          Назад к рецептам
        </button>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1
              className="text-3xl sm:text-4xl font-bold leading-tight mb-2"
              style={{ fontFamily: "var(--font-display)", fontWeight: 900 }}
            >
              {recipe.title}
            </h1>
            <p className="text-sm text-muted-foreground" style={{ fontFamily: "var(--font-body)" }}>
              Добавлено {formattedDate}
            </p>
          </div>

          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="gap-1.5 border-border/60 hover:border-primary/30 hover:bg-primary/5"
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
              </svg>
              Редактировать
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              className="gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10 hover:border-destructive/50"
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              </svg>
              Удалить
            </Button>
          </div>
        </div>
      </div>

      {/* Ingredients */}
      <section className="mb-8 animate-fade-in-up stagger-1">
        <h2
          className="text-xl font-semibold mb-4 flex items-center gap-2"
          style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}
        >
          <svg
            className="w-5 h-5 text-primary/70"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z" />
            <line x1="6" x2="18" y1="17" y2="17" />
          </svg>
          Ингредиенты
        </h2>
        <div className="bg-card/80 border border-border/40 rounded-lg p-5">
          <ul className="space-y-2.5">
            {recipe.ingredients.map((ingredient, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm leading-relaxed"
                style={{ fontFamily: "var(--font-body)" }}
              >
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0" />
                {ingredient}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Instructions */}
      <section className="animate-fade-in-up stagger-2">
        <h2
          className="text-xl font-semibold mb-4 flex items-center gap-2"
          style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}
        >
          <svg
            className="w-5 h-5 text-primary/70"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2v4" />
            <path d="m16.2 7.8 2.9-2.9" />
            <path d="M18 12h4" />
            <path d="m16.2 16.2 2.9 2.9" />
            <path d="M12 18v4" />
            <path d="m4.9 19.1 2.9-2.9" />
            <path d="M2 12h4" />
            <path d="m4.9 4.9 2.9 2.9" />
          </svg>
          Приготовление
        </h2>
        <div className="space-y-4">
          {recipe.instructions.map((step, i) => (
            <div
              key={i}
              className="flex gap-4 bg-card/80 border border-border/40 rounded-lg p-5 hover:border-primary/15 transition-colors duration-200"
            >
              <span
                className="shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {i + 1}
              </span>
              <p
                className="text-sm leading-relaxed pt-0.5"
                style={{ fontFamily: "var(--font-body)" }}
              >
                {step}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
